import { createCustomQuote } from "@stryker/core/api/quotes";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import InfoIcon from "@/assets/icons/info.svg?react";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import {
  AdditionalJobDetails,
  ComponentDetails,
  DefectDetails,
  DesignConditions,
  JobDetails,
} from "@/pages/admin/compositeJob/components";
import useCreateCustomJobForQuoteStore from "@/store/useCreateCustomJobForQuoteStore";

export default function CreateCustomJobForQuote() {
  const { temperatureUnitsOptions } = useConfigOptions();
  const {
    sections,
    setJobDetails,
    setAdditionalJobDetails,
    setDefectDetails,
    setDesignConditions,
    setComponentDetails,
    toggleExpandableSection,
    resetState,
  } = useCreateCustomJobForQuoteStore();

  useEffect(() => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    return () => {
      if (currentPath !== location.pathname || currentSearch !== location.search) {
        // This means the user has navigated away and not just refreshed
        resetState();
      }
    };
  }, [location]);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutate: createCustomQuoteMutate, isPending: isPendingCreateCustomQuote } = useMutation({
    mutationFn: (data: Record<string, unknown>) => createCustomQuote({ data }),
    onSuccess: (response) => {
      resetState();
      navigate(`/quotes/create/?job_number=${response.job_number}`);
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: error });
    },
  });

  const handleCrateCustomQuote = () => {
    const mappedData: Record<string, unknown> = {
      ...sections.jobDetails.data,
      ...sections.additionalJobDetails.data,
      ...sections.componentDetails.data,
      ...sections.defectDetails.data,
      ...sections.designConditions.data,
    };
    createCustomQuoteMutate(mappedData);
  };

  const isTabletOrAbove = useScreens("md", "up");

  return (
    <div>
      <div className="flex items-center gap-5">
        <Link to="/quotes">
          <Typography className="text-gray-400">Quotes</Typography>
        </Link>
        <Typography className="text-gray-400">/</Typography>
        <Typography className="text-primary-600">Crete Custom Quote</Typography>
      </div>
      <Typography
        variant={isTabletOrAbove ? "display-lg" : "text-md"}
        weight="semibold"
        className={cn(!isTabletOrAbove && "ml-20 mt-2")}
      >
        Create Custom Quote
      </Typography>
      <div className="my-9 border-b border-solid border-gray-200 pb-5">
        <Typography variant="text-lg" weight="semibold">
          Main information
        </Typography>
        <div className="flex items-center gap-1">
          <InfoIcon />
          <Typography variant="text-small">Complete each stage of the form</Typography>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <JobDetails
          setJobDetails={setJobDetails}
          savedJobDetails={sections.jobDetails}
          toggleExpandableSection={toggleExpandableSection}
        />

        <AdditionalJobDetails
          setAdditionalJobDetails={setAdditionalJobDetails}
          savedAdditionalJobDetails={sections.additionalJobDetails}
          toggleExpandableSection={toggleExpandableSection}
        />
        <DesignConditions
          temperatureUnitsOptions={temperatureUnitsOptions}
          jobDetails={sections.jobDetails}
          savedDesignConditions={sections.designConditions}
          setDesignConditions={setDesignConditions}
          toggleExpandableSection={toggleExpandableSection}
        />
        <ComponentDetails
          toggleExpandableSection={toggleExpandableSection}
          savedComponentDetails={sections.componentDetails}
          setComponentDetails={setComponentDetails}
          designCode={sections.jobDetails.data.design_code}
        />
        <DefectDetails
          savedDefectDetails={sections.defectDetails}
          toggleExpandableSection={toggleExpandableSection}
          componentDetailsData={sections.componentDetails.data}
          setDefectDetails={setDefectDetails}
        />
      </div>
      <Button disabled={isPendingCreateCustomQuote} onClick={handleCrateCustomQuote} className="mt-10 ml-auto">
        Start Quote
      </Button>
    </div>
  );
}
